import { apiFetch, buildQuery } from './api';
import type {
  AutoscaleRule,
  AutoscaleRuleInput,
  AutoscaleView,
  OpsAutonomy,
  OpsEffectsResponse,
  OpsMission,
  OpsMissionInput,
  OpsProposal,
  OpsProposalStatus,
  OpsRun,
} from './types';

export interface OpsRunScope {
  sqsInstance?: string;
  cluster?: string;
}

// Campos de escopo vazios são omitidos do corpo em vez de viajar como "". O
// servidor trata os dois igual, mas mandar chaves vazias faz todo run sem
// escopo parecer escopado no log de requisição e nos testes de contrato.
export function startOpsRun(
  input: string,
  scope?: OpsRunScope
): Promise<{ runId: string }> {
  const body: Record<string, string> = { input };
  if (scope?.sqsInstance) body.sqsInstance = scope.sqsInstance;
  if (scope?.cluster) body.cluster = scope.cluster;
  return apiFetch<{ runId: string }>('/api/ops/runs', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export interface OpsRunsQuery {
  limit?: number;
  offset?: number;
  // days e hours somam a mesma janela; ausentes (ou zero) significam sem
  // recorte — a lista inteira. Quem calcula o corte é o servidor.
  days?: number;
  hours?: number;
}

export function listOpsRuns(q: OpsRunsQuery = {}): Promise<OpsRun[]> {
  return apiFetch<OpsRun[]>(`/api/ops/runs${buildQuery({ ...q })}`);
}

export function getOpsRun(id: string): Promise<OpsRun> {
  return apiFetch<OpsRun>(`/api/ops/runs/${id}`);
}

export function refineOpsRun(
  id: string,
  input: string
): Promise<{ runId: string }> {
  return apiFetch<{ runId: string }>(`/api/ops/runs/${id}/refine`, {
    method: 'POST',
    body: JSON.stringify({ input }),
  });
}

export function listOpsProposals(
  status?: OpsProposalStatus,
  runId?: string
): Promise<OpsProposal[]> {
  return apiFetch<OpsProposal[]>(
    `/api/ops/proposals${buildQuery({ status, runId })}`
  );
}

export function approveOpsProposal(id: string): Promise<OpsProposal> {
  return apiFetch<OpsProposal>(`/api/ops/proposals/${id}/approve`, {
    method: 'POST',
  });
}

export function rejectOpsProposal(id: string): Promise<OpsProposal> {
  return apiFetch<OpsProposal>(`/api/ops/proposals/${id}/reject`, {
    method: 'POST',
  });
}

export function listOpsMissions(): Promise<OpsMission[]> {
  return apiFetch<OpsMission[]>('/api/ops/missions');
}

export function getOpsMission(id: string): Promise<OpsMission> {
  return apiFetch<OpsMission>(`/api/ops/missions/${id}`);
}

export function createOpsMission(input: OpsMissionInput): Promise<OpsMission> {
  return apiFetch<OpsMission>('/api/ops/missions', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function updateOpsMission(
  id: string,
  input: OpsMissionInput
): Promise<OpsMission> {
  return apiFetch<OpsMission>(`/api/ops/missions/${id}`, {
    method: 'PUT',
    body: JSON.stringify(input),
  });
}

export function deleteOpsMission(id: string): Promise<void> {
  return apiFetch<void>(`/api/ops/missions/${id}`, { method: 'DELETE' });
}

export function pauseOpsMission(
  id: string,
  reason?: string
): Promise<OpsMission> {
  return apiFetch<OpsMission>(`/api/ops/missions/${id}/pause`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
}

export function resumeOpsMission(id: string): Promise<OpsMission> {
  return apiFetch<OpsMission>(`/api/ops/missions/${id}/resume`, {
    method: 'POST',
  });
}

// Dispara a missão agora, fora da cadência. O servidor ignora de propósito o
// interruptor global e a pausa da missão — clicar é decisão humana, não cron —
// mas continua respeitando o orçamento do período (409 quando estourado).
export function runOpsMissionNow(id: string): Promise<{ runId: string }> {
  return apiFetch<{ runId: string }>(`/api/ops/missions/${id}/run`, {
    method: 'POST',
  });
}

export function getOpsAutonomy(): Promise<OpsAutonomy> {
  return apiFetch<OpsAutonomy>('/api/ops/autonomy');
}

export interface OpsEffectsQuery {
  hours?: number;
  days?: number;
  limit?: number;
}

// A janela é calculada no servidor (mesmo motivo de listOpsRuns): o
// relógio do navegador não decide o que é "últimas 24h".
export function getOpsEffects(
  params?: OpsEffectsQuery
): Promise<OpsEffectsResponse> {
  return apiFetch<OpsEffectsResponse>(
    `/api/ops/effects${buildQuery(params as Record<string, unknown>)}`
  );
}

export function getAutoscale(cluster: string): Promise<AutoscaleView> {
  return apiFetch<AutoscaleView>(
    `/api/ops/autoscale${buildQuery({ cluster })}`
  );
}

export function setAutoscaleEnabled(
  cluster: string,
  enabled: boolean
): Promise<AutoscaleView> {
  return apiFetch<AutoscaleView>(
    `/api/ops/autoscale/${encodeURIComponent(cluster)}`,
    { method: 'PUT', body: JSON.stringify({ enabled }) }
  );
}

export function upsertAutoscaleRule(
  cluster: string,
  service: string,
  input: AutoscaleRuleInput
): Promise<AutoscaleRule> {
  return apiFetch<AutoscaleRule>(
    `/api/ops/autoscale/${encodeURIComponent(cluster)}/rules/${encodeURIComponent(service)}`,
    { method: 'PUT', body: JSON.stringify(input) }
  );
}

export function deleteAutoscaleRule(
  cluster: string,
  service: string
): Promise<void> {
  return apiFetch<void>(
    `/api/ops/autoscale/${encodeURIComponent(cluster)}/rules/${encodeURIComponent(service)}`,
    { method: 'DELETE' }
  );
}
